import { db } from '../firebase';
import Sidebar from './Sidebar';
import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Send, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from 'lucide-react';
import { BotIcon } from '../icons/Icons';
import { updateDoc, doc, setDoc, arrayUnion, getDoc } from 'firebase/firestore';
import MarkdownRenderer from '../utils/MarkdownRenderer';
import { DISCLAIMER_TEXT } from './DisclaimerMessage';
import { User } from 'lucide-react';



const config = {
    apiUrl: 'https://faq-zxq5yf5dea-uc.a.run.app'
};

const ProcessingAnimation = ({ status }) => {
  const statusMessages = {
    classifying: "Analyzing your question",
    retrieving: "Finding relevant information",
    generating: "Generating your answer"
  };

  const message = statusMessages[status] || "Processing";

  return (
    <div className="flex flex-col items-start space-y-2 p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center space-x-2">
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
      </div>
      <div className="text-sm text-gray-600">{message}</div>
    </div>
  );
};


// const LoadingAnimation = () => (
//   <div className="flex items-center space-x-2 p-3 bg-gray-100 rounded-lg">
//     <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce"></div>
//     <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
//     <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
//   </div>
// );

const ChatbotInterfaceTest = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [uuid, setUuid] = useState('');
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState({});
  const messagesEndRef = useRef(null);
  const [processingStatus, setProcessingStatus] = useState(null);

  useEffect(() => {
    setUuid(uuidv4());
    setMessages([{ type: 'bot', content: DISCLAIMER_TEXT }]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const logToFirestore = async (data, msgSeq) => {
    try {
      const logRef = doc(db, "logs", data.query_uuid);
      
      // Get the existing document
      const docSnap = await getDoc(logRef);
      
      if (!docSnap.exists()) {
        // If document doesn't exist, create it with initial response array
        await setDoc(logRef, {
          sessionId: uuid,
          responses: [{
            ...data,
            msgSeq,
            timestamp: new Date().toISOString()
          }]
        });
      } else {
        // If document exists, update it by adding new response to array
        await updateDoc(logRef, {
          responses: arrayUnion({
            ...data,
            msgSeq,
            timestamp: new Date().toISOString()
          })
        });
      }
    } catch (error) {
      console.error('Firestore logging error:', error);
    }
  };

  const handleFeedback = async (message, isPositive) => {
    const messageUuid = message.fullResponse?.query_uuid;
    const msgSeq = message.msgSeq;
    
    if (!messageUuid || !msgSeq) {
      console.log('Unable to log feedback: Missing message identifiers');
      return;
    }

    // Create a unique identifier for this message
    const messageId = `${msgSeq}-${messageUuid}`;

    // Toggle feedback if clicking the same button, or switch if clicking the other button
    const currentFeedback = feedback[messageId];
    const newFeedback = currentFeedback === (isPositive ? 'positive' : 'negative') 
      ? null 
      : (isPositive ? 'positive' : 'negative');

    // Update local state
    setFeedback(prev => ({
      ...prev,
      [messageId]: newFeedback
    }));

    try {
      const logRef = doc(db, "logs", messageUuid);
      const docSnap = await getDoc(logRef);

      if (docSnap.exists()) {
        const logData = docSnap.data();
        const updatedResponses = logData.responses.map(response => {
          if (response.msgSeq === msgSeq) {
            return {
              ...response,
              reaction: newFeedback,
              reactionTimestamp: new Date().toISOString()
            };
          }
          return response;
        });

        await updateDoc(logRef, {
          responses: updatedResponses
        });

        console.log(`Feedback updated for message ${messageId}: ${newFeedback}`);
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      // Revert local state if Firebase update fails
      setFeedback(prev => ({
        ...prev,
        [messageId]: currentFeedback
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const newMessage = { type: 'user', content: input };
    setMessages([...messages, newMessage]);
    setInput('');
    setIsLoading(true);
    setProcessingStatus(null);

    if (!agreedToTerms) {
      if (input.trim().toLowerCase() === 'agree') {
        setAgreedToTerms(true);
        setMessages(prev => [...prev, { type: 'bot', content: "Thank you for agreeing to the terms. My name is KolokoloChat and I am here to help provide general Hawaii State Judiciary information. While I aim to provide accurate and helpful resources, please remember each case is different and contains individual situations that I may not be able to assist with. How can I assist you today?" }]);
      } else if (input.toLowerCase() === 'disagree') {
        setMessages(prev => [...prev, { type: 'bot', content: "I'm sorry, but I cannot provide any assistance unless you agree to the terms. Please type 'Agree' if you wish to proceed, or close this chat if you do not agree." }]);
      } else {
        setMessages(prev => [...prev, { type: 'bot', content: "Please respond with 'Agree' or 'Disagree' to the terms." }]);
      }
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.apiUrl}/faq_test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query: input, 
          query_uuid: uuid,
          stream: true
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('Received data:', data); // Debug log
              
              switch (data.status) {
                case 'started':
                case 'classifying':
                case 'retrieving':
                case 'generating':
                  setProcessingStatus(data.status);
                  break;
                
                case 'complete':
                  setIsLoading(false);
                  setProcessingStatus(null);
                  
                  const msgSeq = messages.filter(msg => msg.type === 'bot').length + 1;
                  await logToFirestore(data, msgSeq);

                  // Safely extract the response content
                  let botContent = "I'm sorry, but I couldn't process your request properly.";
                  
                  if (data.response && data.response.error) {
                    botContent = data.response.error;
                  } else if (data.response && data.response.answer) {
                    botContent = data.response.answer;
                  } else if (data.response && data.response.response && data.response.response.answer) {
                    botContent = data.response.response.answer;
                  }

                  setMessages(prev => [...prev, { 
                    type: 'bot', 
                    content: botContent, 
                    fullResponse: data, 
                    msgSeq 
                  }]);
                  break;
                  
                case 'error':
                  throw new Error(data.message || 'An error occurred');
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, 'Raw line:', line);
            }
          }
        }
      }

    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
      setProcessingStatus(null);
      setMessages(prev => [...prev, { 
        type: 'bot', 
        content: "I'm sorry, but I encountered an error while processing your request. Please try again later." 
      }]);
    }
  };

  const toggleDetails = (index) => {
    setExpandedMessage(expandedMessage === index ? null : index);
  };

  const isSpecialMessage = (content) => {
    return content === DISCLAIMER_TEXT || content.startsWith("Thank you for agreeing to the terms.");
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      
      <div className="flex-1 flex flex-col ml-80">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center">
          <img 
            src="BlueSeal_gray_218_bg.jpg" 
            alt="Hawaii State Judiciary" 
            className="h-24 mr-8"
          />
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Judiciary Assistant</h1>
            <p className="text-sm text-gray-600">How can I help you today?</p>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.map((message, index) => (
              <div key={index} 
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-2xl min-w-[200px] rounded-lg p-4 text-left ${
  message.type === 'user' 
    ? 'bg-blue-500 text-white ml-8'
    : 'bg-white shadow-sm border border-gray-200 mr-8'
}`}>
                  {message.type === 'bot' ? (
                    <div className="flex items-left mb-2">
                      <BotIcon className="w-6 h-6 text-blue-600 mr-2" />
                      <span className="font-semibold text-gray-700">Judiciary Assistant</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-start mb-2">
                      <User className="w-6 h-6 text-white mr-1" />
                    </div>
                  )}
                  <div className="prose max-w-none font-['Montserrat']">
                    <MarkdownRenderer>{message.content}</MarkdownRenderer>
                  </div>
                  
                  {message.type === 'bot' && !isSpecialMessage(message.content) && (
                    <div className="mt-4 flex justify-between items-center border-t border-gray-100 pt-3">
                      <button 
                        onClick={() => toggleDetails(index)}
                        className="text-sm text-blue-600 flex items-center hover:underline"
                      >
                        {expandedMessage === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        <span className="ml-1">View Details</span>
                      </button>
                      
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleFeedback(message, true)}
                          className={`p-2 rounded transition-colors ${
                            feedback[`${message.msgSeq}-${message.fullResponse?.query_uuid}`] === 'positive'
                              ? 'text-green-600 bg-green-50'
                              : 'text-gray-400 hover:text-green-600'
                          }`}
                        >
                          <ThumbsUp size={16} />
                        </button>
                        <button 
                          onClick={() => handleFeedback(message, false)}
                          className={`p-2 rounded transition-colors ${
                            feedback[`${message.msgSeq}-${message.fullResponse?.query_uuid}`] === 'negative'
                              ? 'text-red-600 bg-red-50'
                              : 'text-gray-400 hover:text-red-600'
                          }`}
                        >
                          <ThumbsDown size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {expandedMessage === index && (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                        {JSON.stringify(message.fullResponse, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <ProcessingAnimation status={processingStatus} />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 bg-white p-6">
          <div className="max-w-3xl mx-auto">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={agreedToTerms ? "Type your message here..." : "Type 'Agree' or 'Disagree'"}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button 
                type="submit"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotInterfaceTest;
